import { BodyMedium, Dialog, BodyMediumEmphasized } from '@/design-system';
import FlyersContext from '@/context/FlyersContext';
import React, { useContext, useState } from 'react';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '@/config/constants';

interface Props {
  textSearch: string;
  showNotification: boolean;
  setShowNotification: (open: boolean) => void;
}

interface NotificationType {
  [k: string]: {
    title: string;
    confirmButtonText: string;
    description: React.ReactNode;
    onConfirm: () => void;
    onCancel?: () => void;
  };
}

export const DeleteSavedSearchInfo = {
  title: 'Delete save search',
  confirmButtonText: 'Delete',
  description: (
    <BodyMedium>
      You are about to delete a saved search. This action will remove the saved
      search from your list and you will no longer receive notifications for new
      flyers related to this search.
    </BodyMedium>
  ),
};

export const SavedSearchNotification: React.FC<Props> = ({
  textSearch,
  showNotification,
  setShowNotification,
}) => {
  const { savedSearches, toggleSavedSearches } = useContext(FlyersContext);
  const [notificationDisabled, setNotificationDisabled] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const { isSavedSearchEnabled } = useNotificationPreferences();

  const notifications: NotificationType = {
    saveSearch: {
      title: 'Save search',
      confirmButtonText: 'Save search',
      description: (
        <BodyMedium>
          Saving a search allows you to receive notifications for new flyers
          that match your search criteria.
        </BodyMedium>
      ),
      onConfirm: () => toggleSavedSearches(textSearch),
    },
    deleteSaveSearch: {
      ...DeleteSavedSearchInfo,
      onConfirm: () => toggleSavedSearches(textSearch),
    },
    enableNotification: {
      title: 'Enable notification',
      confirmButtonText: 'Enable notification',
      description: (
        <BodyMedium>
          Saving a search allows you to receive notifications for new posting
          that match your search criteria. Enable{' '}
          <BodyMediumEmphasized>
            saved search notifications
          </BodyMediumEmphasized>{' '}
          in your preferences to stay informed.
        </BodyMedium>
      ),
      onConfirm: () => {
        // redirect to notification preferences
        toggleSavedSearches(textSearch);
        navigate(APP_ROUTES.notifications);
      },
      onCancel: () => {
        setShowNotification(false);
        setNotificationDisabled(true);
        setTimeout(() => {
          setShowNotification(true);
        }, 500);
      },
    },
    notificationDisabled: {
      title: 'Save search',
      confirmButtonText: 'Save anyway',
      description: (
        <BodyMedium>
          You're about to save this search term, but notifications are currently
          turned off in your preferences. Would you like to save it without
          receiving notifications for new results?{' '}
        </BodyMedium>
      ),
      onConfirm: () => {
        toggleSavedSearches(textSearch);
        setNotificationDisabled(false);
      },
      onCancel: () => {
        setShowNotification(false);
        setNotificationDisabled(false);
      },
    },
  };

  const getNotificationType = () => {
    const searchExists = savedSearches.find(s => s.search === textSearch);

    if (notificationDisabled) {
      return notifications.notificationDisabled;
    }
    const isSavedSearch = isSavedSearchEnabled();

    // notification turned off and saving a new search
    if (!isSavedSearch && !searchExists) {
      return notifications.enableNotification;
    }
    // delete saved search
    if (searchExists) {
      return notifications.deleteSaveSearch;
    }

    return notifications.saveSearch;
  };

  const onCancel = () => {
    setShowNotification(false);
  };

  if (!showNotification) return null;
  const notification = getNotificationType();

  return (
    <Dialog
      isOpen={showNotification}
      title={notification.title}
      description={notification.description}
      confirmButtonText={notification.confirmButtonText}
      onCancel={notification.onCancel || onCancel}
      onConfirm={() => {
        notification.onConfirm();
        setShowNotification(false);
      }}
    />
  );
};
